import { Block } from '@aws-sdk/client-textract';

export enum ResourceType {
  Policy = 'policy',
  Driver = 'driver',
  Vehicle = 'vehicle',
  Dwelling = 'dwelling',
}

export interface CreatePolicyPayload {
  policyId: string;
  policyUUID: string;
  blocks: Block[];
}

export interface PolicyResponse extends PolicyResource {
  drivers: DriverResource[];
  vehicles: VehicleResource[];
  update: boolean;
}

export interface PolicyResource {
  resourceType: ResourceType.Policy;
  resourceId: string;
  policyUUID: string;
  address?: PrimaryAddress;
  carrierName?: string;
  startDate?: string;
  endDate?: string;
  premium?: number;
  policyType?: string;
  discounts?: Discount[];
  coverages?: ExtractedPolicyCoverage[];
  blocks?: Block[];
}

export interface Discount {
  name: string;
  amount?: string;
}

export interface DriverResource {
  resourceType: ResourceType.Driver;
  resourceId: string;
  firstName: string;
  lastName?: string;
  dateOfBirth?: string;
  usStateCode?: string;
  driverLicenseCode?: string;
  discounts?: Discount[];
}

export interface VehicleResource {
  resourceType: ResourceType.Vehicle;
  resourceId: string;
  name: string;
  make?: string;
  model?: string;
  year?: string;
  vin?: string;
  zipCode?: string;
  primaryUse?: string;
  coverages?: ExtractedPolicyCoverage[];
  discounts?: Discount[];
}

export interface AddVehiclePayload {
  policyId: string;
  name: string;
  make?: string;
  model?: string;
  year?: string;
  vin?: string;
  primaryUse?: string;
  discounts?: Discount[];
  coverages?: ExtractedPolicyCoverage[];
}

export interface AddDriverPayload {
  policyId: string;
  firstName: string;
  lastName?: string;
  driverLicenseCode?: string;
  usStateCode?: string;
  dateOfBirth?: string;
}

export interface UpdateVehiclePayload {
  name?: string;
  make?: string;
  model?: string;
  year?: number;
  vin?: string;
}

export type ExtractedPolicyCoverage = Coverage | CategorizedCoverage;

interface CoveragePayload {
  resourceId: string;
  resourceType: ResourceType;
}

export interface CategorizedCoverage {
  categoryName: string;
  premium: string;
  coverages: Omit<Coverage, 'premium'>[];
}

export interface CreateCoverageCategoryPayload extends CoveragePayload {
  categoryName: string;
  premium?: string;
}

export interface UpdateCoverageCategoryPayload extends CoveragePayload {
  coverageId: number;
  categoryName?: string;
  premium?: string;
}

export interface CategorizedCoveragePayload extends CoveragePayload {
  coverageId: number;
  categorizedCoverage: CategorizedCoverage;
}

export interface Coverage {
  name: string;
  limit?: string;
  deductible?: string;
  premium?: string;
}

export interface AddCoveragePayload extends CoveragePayload {
  coverage: Coverage;
}

export interface UpdateCoveragePayload extends CoveragePayload {
  coverageId: number;
  coverage: Coverage;
}

export interface DeleteCoveragePayload extends CoveragePayload {
  coverageId: number;
}

export interface UpdateDriverPayload {
  firstName?: string;
  lastName?: string;
  driverLicenseCode?: string;
  usStateCode?: string;
  dateOfBirth?: string;
}

export type AddDiscountPayload =
  | AddSingleDiscountPayload
  | AddMultipleDiscountsPayload;

interface AddMultipleDiscountsPayload {
  resourceId: string;
  discounts: string;
}

interface AddSingleDiscountPayload {
  resourceId: string;
  discount: {
    name: string;
    amount?: string;
  };
}

export interface DeleteDiscountPayload {
  resourceId: string;
  discountId: number;
}

export interface PrimaryAddress {
  street?: string;
  street2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}
